import { Timestamp } from '@bufbuild/protobuf'
import { PaymentCollectionType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/payment_collection_type_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CustomerBookerSchema } from '@/components/booking/booker/booker'
import { BookerBookingForm } from '@/components/booking/booker/components/booker-booking-form'
import {
  BookingSelectionForms,
  CompletedForm,
} from '@/components/booking/booker/components/booking-selection-forms'
import { Button } from '@/components/ui/button'
import { useBookingCheckout } from '@/lib/hooks/use-booking-checkout'
import { BookingSelection } from '@/lib/hooks/use-booking-selection'
import { useBookingSetup } from '@/lib/hooks/use-booking-setup'
import { RootStoreContext } from '@/lib/stores/root-store'

type Props = {
  siteSlug: string
  storeConfig?: StoreConfig
  form: any
  submitHandler: (schema: CustomerBookerSchema) => void
  bookingSelection: BookingSelection
  groupClassId?: string
  loading: boolean
  customPrice?: number
}

export const BookerStageBooking = observer(
  ({
    siteSlug,
    storeConfig,
    form,
    submitHandler,
    bookingSelection,
    groupClassId,
    loading,
    customPrice,
  }: Props) => {
    const hasPrice = useMemo(() => {
      if (customPrice === 0) {
        return false
      }
      return bookingSelection.bookingItemsPriceInCents || 0 > 0
    }, [bookingSelection.bookingItemsPriceInCents, customPrice])

    const [formStage, setFormStage] = React.useState<'main' | 'additional'>(
      'main',
    )
    const { t } = useTranslation()
    const { verifyPurchase } = useBookingCheckout({
      siteSlug,
    })

    const { verifySetup } = useBookingSetup({
      siteSlug,
    })

    const {
      bookerStore: {
        stage,
        selectedTimeslot,
        setStage,
        bookingId,
        paymentCollectionType,
        groupWaitlisted,
        setFormResponseIds,
      },
    } = useContext(RootStoreContext)

    const watchEmail = form.watch('email')

    const handleContinueToPay = useCallback(async () => {
      if (!watchEmail) {
        toast.error(t('missing_customer_ema'))
      }

      if (
        watchEmail &&
        selectedTimeslot &&
        bookingSelection.bookingItems.length
      ) {
        await verifyPurchase(
          watchEmail,
          Timestamp.fromDate(selectedTimeslot),
          bookingSelection.eventTypes,
          groupClassId,
        )
        setStage('PAYMENT')
      } else {
        toast.error(t('missing_details'))
      }
    }, [
      bookingSelection.bookingItems.length,
      bookingSelection.eventTypes,
      groupClassId,
      selectedTimeslot,
      setStage,
      t,
      verifyPurchase,
      watchEmail,
    ])

    const isOnLastFormPage = useMemo(() => {
      // There should only be ONE button allowed to be showed on screen
      return (
        stage === 'FORM' &&
        bookingSelection &&
        ((bookingSelection.bookingForms.length > 0
          ? formStage === 'additional'
          : formStage === 'main') ||
          groupWaitlisted)
      )
    }, [bookingSelection, formStage, groupWaitlisted, stage])

    const handleContinueToSetup = useCallback(async () => {
      if (!watchEmail) {
        toast.error(t('missing_customer_ema'))
      }

      if (
        watchEmail &&
        selectedTimeslot &&
        bookingSelection.bookingItems.length
      ) {
        await verifySetup(
          watchEmail,
          Timestamp.fromDate(selectedTimeslot),
          bookingSelection.eventTypes,
          groupClassId,
        )
        setStage('PAYMENT')
      } else {
        toast.error(t('missing_details'))
      }
    }, [
      bookingSelection.bookingItems.length,
      bookingSelection.eventTypes,
      groupClassId,
      selectedTimeslot,
      setStage,
      t,
      verifySetup,
      watchEmail,
    ])

    const navigateToAdditionalForm = useCallback(() => {
      setFormStage('additional')
    }, [])

    // This function executes the same logic as the individual submit buttons
    // but this callback allows the additional form to be submitted prior
    const onAdditionalFormSuccess = useCallback(
      async (completedForms: CompletedForm[]) => {
        // TODO fix
        if (completedForms.length === 0) {
          return
        }

        // Extract and store form response IDs
        const responseIds = completedForms.map((form) => form.formResponseId)
        setFormResponseIds(responseIds)

        if (hasPrice) {
          if (
            storeConfig?.paymentCollectionType ===
            PaymentCollectionType.COLLECT_UPFRONT
          ) {
            return await handleContinueToPay()
          } else if (
            storeConfig?.paymentCollectionType ===
            PaymentCollectionType.CAPTURE_DETAILS
          ) {
            return await handleContinueToSetup()
          }
        }
        return form.handleSubmit(submitHandler)()
      },
      [
        form,
        hasPrice,
        submitHandler,
        storeConfig?.paymentCollectionType,
        handleContinueToPay,
        handleContinueToSetup,
        setFormResponseIds,
      ],
    )

    if (formStage === 'additional' && bookingSelection.bookingForms.length) {
      // Disable notifications for any form submission done in page
      return (
        <BookingSelectionForms
          bookingSelection={bookingSelection}
          storeConfig={storeConfig}
          siteSlug={siteSlug}
          source={`/${siteSlug}/bookings`}
          onAllFormsSubmitted={onAdditionalFormSuccess}
        />
      )
    }

    // Render the action button based on current state
    const renderActionButton = () => {
      // If not on the last form page, render the "Next" button
      if (!isOnLastFormPage) {
        return (
          <Button
            className="mb-2 mt-4"
            onClick={navigateToAdditionalForm}
            type="button"
            loading={loading}
            disabled={!form?.formState?.isValid}
          >
            {t('next')}
          </Button>
        )
      }

      // Handle waitlist case
      if (groupWaitlisted) {
        return (
          <Button className="mb-2 mt-4" type="submit" loading={loading}>
            {t('Join waitlist')}
          </Button>
        )
      }

      // Handle payment collection
      if (hasPrice) {
        if (paymentCollectionType === PaymentCollectionType.COLLECT_UPFRONT) {
          return (
            <>
              <div className="my-2 mt-4 text-sm text-muted-foreground">
                {t(
                  'by_clicking_proceed_to_payment_you_acknowledge_that_you_have_reviewed_the_booking_details_carefully_and_that_all_information_provided_is_accurate_and_complete',
                )}
              </div>
              <Button
                className="mb-2 mt-4"
                type="button"
                onClick={handleContinueToPay}
                loading={loading}
                disabled={!form?.formState?.isValid}
              >
                {t('proceed_to_payment')}
              </Button>
            </>
          )
        } else if (
          paymentCollectionType === PaymentCollectionType.CAPTURE_DETAILS
        ) {
          return (
            <>
              <div className="my-2 mt-4 text-sm text-muted-foreground">
                {t('by_clicking_confirm_')}
              </div>
              <Button
                className="mb-2 mt-4"
                type="button"
                onClick={handleContinueToSetup}
                loading={loading}
                disabled={!form?.formState?.isValid}
              >
                {t('confirm_details')}
              </Button>
            </>
          )
        }
      }

      // Default case - regular submit button
      return (
        <Button className="mb-2 mt-4" type="submit" loading={loading}>
          {bookingId ? t('update_event') : t('schedule_event')}
        </Button>
      )
    }

    return (
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(submitHandler)}
          className="flex size-full flex-col"
        >
          {stage === 'FORM' && (
            <BookerBookingForm
              bookingSelection={bookingSelection}
              control={form.control}
              watch={form.watch}
              readOnly={!!bookingId}
              waitlisted={groupWaitlisted}
            />
          )}

          {/* Refactored action button logic */}
          {renderActionButton()}
        </form>
      </FormProvider>
    )
  },
)
